import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { Comment, UserRole } from '../types'

interface CommentSystemProps {
  postId: string
  comments: Comment[]
  onAddComment: (content: string, replyToCommentId?: string) => void
  onVoteComment: (commentId: string, type: 'upvote' | 'downvote') => void
  onEditComment: (commentId: string, newContent: string) => void
  onDeleteComment: (commentId: string) => void
  maxDepth?: number
}

interface CommentItemProps {
  comment: Comment
  allComments: Comment[]
  depth: number
  maxDepth: number
  onReply: (commentId: string, content: string) => void
  onVote: (commentId: string, type: 'upvote' | 'downvote') => void
  onEdit: (commentId: string, newContent: string) => void
  onDelete: (commentId: string) => void
}

function getRoleColor(role: string): string {
  switch (role) {
    case 'admin': return '#e53e3e'
    case 'moderator': return '#9f7aea'
    case 'verified_vendor': return '#00d166'
    case 'trusted': return '#4299e1'
    default: return '#a0aec0'
  }
}

function getRoleBadge(role: string): string {
  switch (role) {
    case 'admin': return '👑'
    case 'moderator': return '🛡️'
    case 'verified_vendor': return '✅'
    case 'trusted': return '⭐'
    default: return ''
  }
}

function formatTimestamp(date: Date): string {
  const now = new Date()
  const diff = now.getTime() - date.getTime()
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)

  if (minutes < 1) return 'Just now'
  if (minutes < 60) return `${minutes}m ago`
  if (hours < 24) return `${hours}h ago`
  if (days < 7) return `${days}d ago`
  return date.toLocaleDateString()
}

function CommentItem({ 
  comment, 
  allComments, 
  depth, 
  maxDepth, 
  onReply, 
  onVote, 
  onEdit, 
  onDelete 
}: CommentItemProps) {
  const { user, isLoggedIn } = useAuth()
  const [showReplyForm, setShowReplyForm] = useState(false)
  const [replyContent, setReplyContent] = useState('')
  const [isEditing, setIsEditing] = useState(false)
  const [editContent, setEditContent] = useState(comment.content)
  const [showReplies, setShowReplies] = useState(true)

  const replies = allComments.filter(c => c.replyToCommentId === comment.id)
  const canEdit = user?.id === comment.authorId || user?.role === 'moderator' || user?.role === 'admin'
  const canDelete = user?.id === comment.authorId || user?.role === 'moderator' || user?.role === 'admin'

  const handleSubmitReply = (e: React.FormEvent) => {
    e.preventDefault()
    if (replyContent.trim()) {
      onReply(comment.id, replyContent.trim())
      setReplyContent('')
      setShowReplyForm(false)
    }
  }

  const handleSubmitEdit = (e: React.FormEvent) => {
    e.preventDefault()
    if (editContent.trim() && editContent !== comment.content) {
      onEdit(comment.id, editContent.trim())
      setIsEditing(false)
    }
  }

  const handleCancelEdit = () => {
    setEditContent(comment.content)
    setIsEditing(false)
  }

  // Process mentions in content
  const processedContent = comment.content.replace(
    /@(\w+)/g, 
    '<span class="mention">@$1</span>'
  )

  return (
    <div className={`comment-item depth-${depth}`} style={{ marginLeft: `${depth * 20}px` }}>
      <div className="comment-main">
        <div className="comment-sidebar">
          <Link to={`/profile/${comment.authorId}`} className="comment-avatar">
            <div className="avatar-placeholder">
              {comment.authorUsername[0].toUpperCase()}
            </div>
          </Link>
          
          <div className="comment-votes">
            <button 
              onClick={() => onVote(comment.id, 'upvote')}
              className={`vote-btn upvote ${!isLoggedIn ? 'disabled' : ''}`}
              disabled={!isLoggedIn}
              title="Upvote comment"
            >
              ↑
            </button>
            <span className="vote-score">{comment.likes}</span>
            <button 
              onClick={() => onVote(comment.id, 'downvote')}
              className={`vote-btn downvote ${!isLoggedIn ? 'disabled' : ''}`}
              disabled={!isLoggedIn}
              title="Downvote comment"
            >
              ↓
            </button>
          </div>
        </div>

        <div className="comment-content">
          <div className="comment-header">
            <Link 
              to={`/profile/${comment.authorId}`}
              className="comment-author"
              style={{ color: getRoleColor(comment.authorRole) }}
            >
              {getRoleBadge(comment.authorRole)} {comment.authorUsername}
            </Link>
            <span className="comment-date">{formatTimestamp(comment.dateCreated)}</span>
            {comment.dateEdited && (
              <span className="comment-edited" title={`Edited ${comment.dateEdited.toLocaleString()}`}>
                (edited)
              </span>
            )}
          </div>

          <div className="comment-text">
            {isEditing ? (
              <form onSubmit={handleSubmitEdit} className="edit-form">
                <textarea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  rows={3}
                  required
                  minLength={1}
                  className="edit-textarea"
                />
                <div className="edit-actions">
                  <button type="button" onClick={handleCancelEdit} className="btn-secondary small">
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary small">
                    Save
                  </button>
                </div>
              </form>
            ) : (
              <div 
                className="comment-content-text"
                dangerouslySetInnerHTML={{ __html: processedContent }}
              />
            )}
          </div>

          <div className="comment-actions">
            {isLoggedIn && !isEditing && (
              <button 
                onClick={() => setShowReplyForm(!showReplyForm)}
                className="action-btn"
              >
                Reply
              </button>
            )}
            
            {canEdit && !isEditing && (
              <button 
                onClick={() => setIsEditing(true)}
                className="action-btn"
              >
                Edit
              </button>
            )}
            
            {canDelete && (
              <button 
                onClick={() => onDelete(comment.id)}
                className="action-btn delete"
              >
                Delete
              </button>
            )}

            {replies.length > 0 && (
              <button 
                onClick={() => setShowReplies(!showReplies)}
                className="action-btn toggle-replies"
              >
                {showReplies ? 'Hide' : 'Show'} replies ({replies.length})
              </button>
            )}
          </div>

          {/* Reply Form */}
          {showReplyForm && (
            <form onSubmit={handleSubmitReply} className="reply-form">
              <textarea
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                placeholder={`Reply to ${comment.authorUsername}...`}
                rows={3}
                required
                minLength={1}
              />
              <div className="reply-actions">
                <button 
                  type="button" 
                  onClick={() => setShowReplyForm(false)}
                  className="btn-secondary small"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary small">
                  Reply
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Nested Replies */}
      {showReplies && replies.length > 0 && depth < maxDepth && (
        <div className="comment-replies">
          {replies.map(reply => (
            <CommentItem
              key={reply.id}
              comment={reply}
              allComments={allComments}
              depth={depth + 1}
              maxDepth={maxDepth}
              onReply={onReply}
              onVote={onVote}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}

      {/* Max depth reached - show "continue thread" link */}
      {replies.length > 0 && depth >= maxDepth && (
        <div className="continue-thread">
          <Link to={`#comment-${comment.id}`} className="continue-link">
            Continue this thread →
          </Link>
        </div>
      )}
    </div>
  )
}

export default function CommentSystem({ 
  postId, 
  comments, 
  onAddComment, 
  onVoteComment, 
  onEditComment, 
  onDeleteComment,
  maxDepth = 5 
}: CommentSystemProps) {
  const { isLoggedIn } = useAuth()
  const [newCommentContent, setNewCommentContent] = useState('')
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'top'>('top')

  // Filter top-level comments (not replies)
  const topLevelComments = comments.filter(c => !c.replyToCommentId)

  // Sort comments
  const sortedComments = [...topLevelComments].sort((a, b) => {
    switch (sortBy) {
      case 'newest':
        return b.dateCreated.getTime() - a.dateCreated.getTime()
      case 'oldest':
        return a.dateCreated.getTime() - b.dateCreated.getTime()
      case 'top':
        return b.likes - a.likes
      default:
        return 0
    }
  })

  const handleSubmitComment = (e: React.FormEvent) => {
    e.preventDefault()
    if (newCommentContent.trim()) {
      onAddComment(newCommentContent.trim())
      setNewCommentContent('')
    }
  }

  const handleReply = (commentId: string, content: string) => {
    onAddComment(content, commentId)
  }

  return (
    <div className="comment-system">
      <div className="comments-header">
        <h3>Comments ({comments.length})</h3>
        
        {comments.length > 1 && (
          <div className="comments-sort">
            <label htmlFor="sort-comments">Sort by:</label>
            <select
              id="sort-comments"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'newest' | 'oldest' | 'top')}
            >
              <option value="top">Top rated</option>
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
            </select>
          </div>
        )}
      </div>

      {/* New Comment Form */}
      {isLoggedIn ? (
        <form onSubmit={handleSubmitComment} className="new-comment-form">
          <textarea
            value={newCommentContent}
            onChange={(e) => setNewCommentContent(e.target.value)}
            placeholder="Share your thoughts... You can mention users with @username"
            rows={4}
            required
            minLength={1}
          />
          <div className="comment-form-actions">
            <div className="form-tips">
              <span>💡 Tip: Use @username to mention other users</span>
            </div>
            <button 
              type="submit" 
              className="btn-primary"
              disabled={!newCommentContent.trim()}
            >
              Post Comment
            </button>
          </div>
        </form>
      ) : (
        <div className="login-prompt">
          <p>You must be logged in to comment.</p>
          <Link to="/login" className="btn-primary">Login</Link>
        </div>
      )}

      {/* Comments List */}
      <div className="comments-list">
        {sortedComments.length === 0 ? (
          <div className="no-comments">
            <p>No comments yet. Be the first to share your thoughts!</p>
          </div>
        ) : (
          sortedComments.map(comment => (
            <CommentItem
              key={comment.id}
              comment={comment}
              allComments={comments}
              depth={0}
              maxDepth={maxDepth}
              onReply={handleReply}
              onVote={onVoteComment}
              onEdit={onEditComment}
              onDelete={onDeleteComment}
            />
          ))
        )}
      </div>
    </div>
  )
}